/* eslint-disable max-lines */
import {ApolloCache, gql, MutationFunctionOptions, useApolloClient, useMutation} from "@apollo/client";
import {useCallback} from "react";
import {
  addIdentity, createOptimisticResponse, findParentOfObject, moveItemInCache, rewriteCacheQuery
} from "../../apollo/utils";
import {
  Project,
  Challenge,
  ChallengeProject,

  ChallengeFilterInput,
  MutationAddProjectArgs, MutationDeleteProjectArgs,
  MutationAddChallengeArgs,
  MutationUpdateChallengeArgs,
  MutationDeleteChallengeArgs,
  MutationCopyChallengeArgs,
  MutationCopyProjectArgs
} from "../../schema";
import ChallengesTableTable from "../../components/editor/challenges-table/ChallengesTable.table";
import ChallengeTableItem from "../../components/editor/challenges-table/ChallengeTableItem";
import ChallengeEditDropdown from "../../components/editor/challenges-table/ChallengeEditDropdown";

export type queryVars = {
  filter: ChallengeFilterInput;
};

export type queryData = {
  items: ChallengeProject[];
  projects: Project[];
};

export const query = gql`
  query EditorChallengeListSceneQuery($filter: ChallengeFilterInput) {
    items: getChallenges(filter: $filter) {
      ...ChallengesTableTable
    }
    projects: getProjects {
      ...ChallengesTableTableProjects
    }
  }
  ${ChallengesTableTable.fragments.root}
  ${ChallengesTableTable.fragments.projects}
`;

export const getCompetenceTestTemplateUrlQuery = gql`
  query GetCompetenceTestTemplateUrlQuery {
    templateUrl: getCompetenceTestTemplateUrl
  }
`;
export const competenciesQuery = gql`
  query EditorChallengeListSceneCompetenciesQuery {
    competencies: getCompetencies {
      id
      name
    }
  }
`;

export function useAddChallengeMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{challenge: Challenge}, MutationAddChallengeArgs>, "fetchPolicy">;
  return [
    useCallback(
      (opts: opts) => {
        const projectId = opts.variables?.data.projectId;
        return client.mutate({
          mutation: addChallengeMutation,
          update: (cache, mutationResult) => {
            const challenge = mutationResult.data?.challenge;

            if (!challenge) {
              return;
            }

            if (projectId) {
              addChallengeToCachedProject(cache, projectId, challenge)
            }

            addChallengeToCachedChallenges(cache, challenge);
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}

const addChallengeMutation = gql`
  mutation AddChallengeMutation($data: ChallengeInput!) {
    challenge: addChallenge(data: $data) {
      __typename
      id
      ...ChallengesTableTableChallenge
    }
  }

  fragment ChallengesTableTableChallenge on Challenge {
    projectId
    projectName
    languageCode
    lastUpdatedAt
    ...ChallengeTableItem
    ...ChallengeEditDropdown
  }

  ${ChallengeTableItem.fragments.root}
  ${ChallengeEditDropdown.fragments.root}
`;

export function useCopyChallengeMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{challenge: Challenge}, MutationCopyChallengeArgs> & {
     projectId?: string }, "fetchPolicy">;
  return [
    useCallback(
      (opts: opts) => {
        return client.mutate({
          mutation: copyChallengeMutation,
          update: (cache, mutationResult) => {
            const challenge = mutationResult.data?.challenge;
            if (!challenge) {
              return;
            }

            // If challenge belongs to a project, update the project cache
            if (challenge.projectId) {
              addChallengeToCachedProject(cache, challenge.projectId, challenge);
            } else {
              // Otherwise update the general challenges cache
              addChallengeToCachedChallenges(cache, challenge);
            }
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}


export function useDeleteChallengeMutation() {
  const [mutate] = useMutation<{ deleted: boolean }, MutationDeleteChallengeArgs>(deleteChallengeMutation);

  type opts = Omit<MutationFunctionOptions<{deleted: boolean}, MutationDeleteChallengeArgs>, "fetchPolicy">;
  return [
    useCallback(
      async (opts: opts) => {
        const challengeId = opts.variables?.id;

        if (!challengeId) {
          return;
        }

        try {
          const result = await mutate({
            ...opts,
            update: (cache, mutationResult) => {
              removeChallengeFromCache(cache, {
                __typename: "Challenge",
                id: challengeId,
              });
            },
          });

          return result;
        } catch (error) {
          console.warn(error)
        }
      },
      [mutate],
    ),
  ];
}

export function useImportChallengeCsvMutation() {
  const client = useApolloClient();

  type opts = Omit<
    MutationFunctionOptions<{challenge: Challenge}, {attachment: File; languageCode: string}>,
    "fetchPolicy"
  >;
  return [
    useCallback(
      (opts: opts) => {
        return client.mutate({
          mutation: importChallengeCsvMutation,
          update: (cache, mutationResult) => {
            const challenge = mutationResult.data?.challenge;

            if (!challenge) {
              return;
            }

            addChallengeToCachedChallenges(cache, challenge);
          },
          ...opts,
        });
      },
      [client],
    ),
  ];
}

const copyChallengeMutation = gql`
  mutation CopyChallengeMutation($id: ID!) {
    challenge: copyChallenge(id: $id) {
      __typename
      id
      ...ChallengesTableTableChallenge
    }
  }

  fragment ChallengesTableTableChallenge on Challenge {
    projectId
    projectName
    languageCode
    lastUpdatedAt
    ...ChallengeTableItem
    ...ChallengeEditDropdown
  }

  ${ChallengeTableItem.fragments.root}
  ${ChallengeEditDropdown.fragments.root}
`;

const deleteChallengeMutation = gql`
  mutation DeleteChallengeMutation($id: ID!) {
    deleted: deleteChallenge(id: $id)
  }
`;

const importChallengeCsvMutation = gql`
  mutation ImportChallengeCsvMutation($attachment: Upload!, $languageCode: LanguageCode!) {
    challenge: importChallengeCsv(attachment: $attachment, languageCode: $languageCode) {
      ...ChallengesTableTable
    }
  }
  ${ChallengesTableTable.fragments.root}
`;

export function useAddProjectMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{project: Project}, MutationAddProjectArgs>, "fetchPolicy">
  return [useCallback((opts: opts) => {
    return client.mutate({
      mutation: addProjectMutation,
      update: (cache, mutationResult) => {
        const project = mutationResult.data?.project;

        if (!project) {
          return;
        }

        addChallengeProjectToCachedChallenges(cache, project)
      },
      ...opts
    });
  }, [client])];
}


const addProjectMutation = gql`
  mutation AddProjectMutation($data: ProjectInput!) {
    project: addProject(data: $data) {
      __typename
      id
      ...ChallengesTableTable
    }
  }

  ${ChallengesTableTable.fragments.root}
`;

export function useCopyProjectMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{project: Project}, MutationCopyProjectArgs>, "fetchPolicy">
  return [useCallback((opts: opts) => {

    return client.mutate({
      mutation: copyProjectMutation,
      update: (cache, mutationResult) => {
        const project = mutationResult.data?.project;

        if (!project) {
          return;
        }

        addChallengeProjectToCachedChallenges(cache, project)
      },
      ...opts
    });
  }, [client])];
}

const copyProjectMutation = gql`
  mutation CopyProjectMutation($id: ID!) {
    project: copyProject(id: $id) {
      __typename
      id
      ...ChallengesTableTable
    }
  }

  ${ChallengesTableTable.fragments.root}
`;

export function useDeleteProjectMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<{deleted: boolean}, MutationDeleteProjectArgs>, "fetchPolicy">
  return [useCallback((opts: opts) => {
    const projectId = opts.variables?.id;

    if (!projectId) {
      return;
    }

    return client.mutate({
      mutation: deleteProjectMutation,
      update: (cache, mutationResult) => {
        const result = mutationResult.data?.deleted;

        if (!result) {
          return;
        }

        removeChallengeProjectFromCachedChallenges(cache, {
          __typename: "Project",
          id: projectId
        })
      },
      ...opts
    });
  }, [client])];
}

const deleteProjectMutation = gql`
  mutation DeleteProjectMutation($id: ID!) {
    deleted: deleteProject(id: $id)
  }
`;

export function useMoveChallengeMutation() {
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<
    {challenge: Pick<Challenge, "__typename" | "id">},
    MutationUpdateChallengeArgs
  >, "fetchPolicy">
  return [useCallback((opts: opts) => {
    const projectId = opts.variables?.data.projectId;

    return client.mutate({
      mutation: updateChallengeMutation,
      update: (cache, mutationResult) => {
        const challenge = mutationResult.data?.challenge;

        if (!challenge) {
          return;
        }

        removeChallengeFromCache(cache, challenge)

        if (projectId) {
          addChallengeToCachedProject(cache, projectId, challenge)
        } else if (projectId === null) {
          addChallengeProjectToCachedChallenges(cache, challenge)
        }
      },
      ...opts
    });
  }, [client])];
}

export function useReorderChallengeMutation() {
  // return useMutation<
  //   {challenge: Pick<Challenge, "__typename" | "id">},
  //   MutationUpdateChallengeArgs
  // >(updateChallengeMutation);
  const client = useApolloClient();

  type opts = Omit<MutationFunctionOptions<
    {challenge: Pick<Challenge, "__typename" | "id">},
    MutationUpdateChallengeArgs
  >, "fetchPolicy">
  return [useCallback((opts: opts) => {
    const beforeChallengeId = opts.variables?.data?.beforeChallengeId;
    const challengeID = opts.variables?.id

    const fragmentArgs = {
      id: `Challenge:${challengeID}`,
      fragmentName: "UpdatedChallenge",
      fragment: gql`
        fragment UpdatedChallenge on Challenge {
        ...ChallengesTableTable
        }

        ${ChallengesTableTable.fragments.root}
      `
    }

    const optimisticResponseChallenge = createOptimisticResponse<Challenge>(
      client, fragmentArgs
    )

    return client.mutate({
      mutation: updateChallengeMutation,
      optimisticResponse: {
        challenge: optimisticResponseChallenge
      },
      update: (cache, mutationResult) => {

        const challenge = mutationResult.data?.challenge;

        if (!challenge) {
          return;
        }

        if (beforeChallengeId !== undefined) {
          moveChallengeInCache(cache, challenge, beforeChallengeId)
          return;
        }
      },
      ...opts
    });
  }, [client])];
}

const updateChallengeMutation = gql`
  mutation UpdateChallengeMutation($id: ID!, $data: ChallengeInput!) {
    challenge: updateChallenge(id: $id, data: $data) {
      __typename
      id
    }
  }
`;

// Cache update functions
function removeChallengeFromCache(cache: ApolloCache<any>, challenge: Pick<Challenge, "__typename" | "id">) {
  const challengeID = challenge.id

  if (typeof findParentOfObject === "function") {
    const parentalProject = findParentOfObject<Partial<Project>>(cache, challenge, "Project", "challenges")
    if (parentalProject && cache.identify(parentalProject)) {
      cache.modify({
        id: cache.identify(parentalProject),
        fields: {
          challenges(cachedChallenges: Challenge[], {readField}) {
            return cachedChallenges.filter((challenge) => readField("id", challenge) !== challengeID)
          },
        },
      });
    } else {
      removeChallengeProjectFromCachedChallenges(cache, challenge)
    }
  }
}

function addChallengeToCachedProject(
  cache: ApolloCache<any>,
  projectId: string | null,
  challenge: Pick<Challenge, "__typename" | "id">
) {
  if (!projectId) {
    return;
  }

  try {
    cache.modify({
      id: `Project:${projectId}`,
      fields: {
        challenges(cachedChallenges= []) {
          const challengeId = cache.identify(challenge);
          const challengeRef = {__ref: challengeId};
          const challengeExists = cachedChallenges.some(
            (existingChallenge: any) => existingChallenge && existingChallenge.__ref === challengeId,
          );

          if (challengeExists) {
            return cachedChallenges;
          }

          const newChallenge = [...cachedChallenges, challengeRef];
          return newChallenge;
        },
      },
    });
  } catch (error) {
    console.warn(error)
  }
}

function addChallengeToCachedChallenges(
  cache: ApolloCache<any>,
  item: Pick<ChallengeProject, "__typename" | "id">
) {
  const filterVariations = [
    {languageCode: null, challengeMode: null, name: ""},
    {languageCode: null, challengeMode: null, name: null},
  ];

  filterVariations.forEach((filter) => {
    try {
      rewriteCacheQuery(
        cache,
        {
          query: gql`
            query ($filter: ChallengeFilterInput!) {
              challenges: getChallenges(filter: $filter) {
                id
                __typename
              }
            }
          `,
          variables: {filter},
        },
        (cacheData: {challenges: ChallengeProject[]}) => {
          if (!cacheData?.challenges) return;
          return addIdentity(cacheData, "challenges", item);
        },
      );
    } catch (error) {
      console.warn(error)
    }
  });
}

function addChallengeProjectToCachedChallenges(
  cache: ApolloCache<any>,
  item: Pick<ChallengeProject, "__typename" | "id">
) {
  rewriteCacheQuery(cache, {
    query: gql`query($filter: ChallengeFilterInput!) {
      challenges: getChallenges(filter: $filter) {
        id
        __typename
      }
    }`,
    variables: {
      filter: {
        languageCode: null,
        challengeMode: null,
        name: ""
      }
    }
  }, (cacheData: {
    challenges: ChallengeProject[]
  }) => {
    if (!cacheData || !cacheData.challenges) return;
    return addIdentity(cacheData, "challenges", item)
  })
}

function removeChallengeProjectFromCachedChallenges(
  cache: ApolloCache<any>,
  item: Pick<ChallengeProject, "__typename" | "id">
) {
  try {
    const challengeId = cache.identify(item);
    cache.evict({
      id: challengeId,
    });
  } catch (error) {
    console.warn(error);
  }

  try {
    const cacheData = cache.extract();
    const challengeRefToRemove = cache.identify(item);
    const rootQuery = cacheData.ROOT_QUERY;

    if (rootQuery) {
      const projectQueryKey = Object.keys(rootQuery).find((key) => key.startsWith("getProject("));

      if (projectQueryKey) {
        const projectRef = rootQuery[projectQueryKey];
        if (projectRef && projectRef.__ref) {
          const projectId = projectRef.__ref;

          cache.modify({
            id: projectId,
            fields: {
              challenges: (existingChallenges: any[]) => {
                if (!Array.isArray(existingChallenges)) return existingChallenges;

                const filtered = existingChallenges.filter((challengeRef) => {
                  const shouldKeep = challengeRef?.__ref !== challengeRefToRemove && challengeRef?.__ref !== undefined;
                  return shouldKeep;
                });
                return filtered;
              },
            },
          });
        }
      }
    }

    cache.gc();
  } catch (error) {
    console.warn(error);
  }
}

function moveChallengeInCache(
  cache: ApolloCache<any>, challenge: Pick<Challenge, "__typename" | "id">, beforeChallengeId: Challenge["id"] | null
) {
  return moveItemInCache<Project, Challenge>(cache, challenge, beforeChallengeId, "Project", "challenges");
}
